/*
 * Based on three.js SAOShader.js
 * Modifications copyright Higharc Inc. 2019
 *
 * The shader will output (1.0 - ambient occlusion) in the red channel.
 * Blending can be used to apply the AO to a regular rendering.
 */
import * as THREE from "three";
import type { ScreenSpaceViewNormalUniforms, ScreenSpaceViewPositionUniforms } from "./screenSpaceHelpersGLSL";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

interface SAOShader2Uniforms extends ScreenSpaceViewPositionUniforms, ScreenSpaceViewNormalUniforms {
  invSize: THREE.IUniform;

  scale: THREE.IUniform;
  intensity: THREE.IUniform;
  bias: THREE.IUniform;

  minResolution: THREE.IUniform;
  worldSpaceScale: THREE.IUniform;
  maxScreenSpaceKernelRadius: THREE.IUniform;
  randomSeed: THREE.IUniform;

  assumedThickness: THREE.IUniform;
}

interface SAOShader2Type {
  defines: {
    NUM_SAMPLES: number; // eslint-disable-line @typescript-eslint/naming-convention
    NUM_RINGS: number; // eslint-disable-line @typescript-eslint/naming-convention
    NORMAL_TEXTURE: number; // eslint-disable-line @typescript-eslint/naming-convention
    NORMAL_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
    DEPTH_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
    DEPTH_TEX_CONTAINS_VIEWZ: number; // eslint-disable-line @typescript-eslint/naming-convention
    PERSPECTIVE_CAMERA: number; // eslint-disable-line @typescript-eslint/naming-convention

    ASSUME_THICK_GEOMETRY: number; // eslint-disable-line @typescript-eslint/naming-convention

    SIMPLIFIED_SAMPLING_REGION: number; // eslint-disable-line @typescript-eslint/naming-convention

    OUTPUT_NORMALS: number; // eslint-disable-line @typescript-eslint/naming-convention

    DEPTH_MIPS: number; // eslint-disable-line @typescript-eslint/naming-convention
  };
  uniforms: SAOShader2Uniforms;
  vertexShader: string;
  fragmentShader: string;
}

export const SAOShader2: SAOShader2Type = {
  defines: {
    ...screenSpaceViewPositionDefineDefaults,
    ...screenSpaceViewNormalDefineDefaults,
    NORMAL_TEXTURE: 0,

    NUM_SAMPLES: 9,
    NUM_RINGS: 2,

    // If this is on, SAO assumes that geometry is thick in the camera forward direction.
    // This can create unrealistic haloing around objects that are actually thin,
    // but renders better results for walls displayed from a top-down orthogonal camera for example.
    ASSUME_THICK_GEOMETRY: 0,

    // Normal sampling region is sphere shaped.
    // Simplified sampling region is rectangular prism shaped.
    // The rectangular prism is cheaper to evaluate but is more likely jarring transitions when geometry moves across
    // the edge of the sampling region.
    SIMPLIFIED_SAMPLING_REGION: 0,

    OUTPUT_NORMALS: 0,

    // Number of depth mip levels to use.
    // They're passed to the shader as separate samplers, since due to WebGL 1.0 limitations we can't use a mipmapped NPOT texture.
    DEPTH_MIPS: 1,
  },
  uniforms: {
    ...screenSpaceViewPositionUniformDefaults,
    ...screenSpaceViewNormalUniformDefaults,

    // Inverse of sampled depth buffer size in pixels - can also be thought as the size of one pixel in UV space.
    invSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },

    scale: { value: 1.0 },
    intensity: { value: 0.1 },
    bias: { value: 0.5 },

    minResolution: { value: 0.0 },
    worldSpaceScale: { value: 0.4 },
    maxScreenSpaceKernelRadius: { value: 0.1 },
    randomSeed: { value: 0.0 },

    assumedThickness: { value: 0.0 },
  },
  vertexShader: UVVertexShader,

  fragmentShader: `#include <common>
    #include <packing>

    varying vec2 vUv;

    // Gets set to vUv with jitter applied.
    vec2 centerUv;

    uniform sampler2D tDepth[DEPTH_MIPS];

    float unpackRGBAToDepthExceptMip0( const in vec4 depth, int mipIndex ) {
      return mipIndex > 0 ? unpackRGBAToDepth( depth ) : depth.x;
    }

    // This is a macro and not a function to work within WebGL 1.0 shader limitations.
    // We can't pass the index to the sampler array to a function.
    #if DEPTH_TEX_CONTAINS_VIEWZ == 1 || (DEPTH_PACKING == 0 && DEPTH_MIPS <= 1)
      #define GET_DEPTH(mipIndex, screenPosition) texture2D( tDepth[mipIndex], screenPosition ).x
    #elif DEPTH_PACKING == 1
      #define GET_DEPTH(mipIndex, screenPosition) unpackRGBAToDepth( texture2D( tDepth[mipIndex], screenPosition ) )
    #else
      // Mip levels after the 0 level are always packed to RGBA.
      #define GET_DEPTH(mipIndex, screenPosition) unpackRGBAToDepthExceptMip0( texture2D( tDepth[mipIndex], screenPosition ), mipIndex )
    #endif

    ${screenSpaceViewPositionGLSL}
    ${screenSpaceViewNormalGLSL}

    uniform vec2 invSize;

    uniform float scale;
    uniform float intensity;
    uniform float bias;
    uniform float worldSpaceScale;
    uniform float minResolution;
    uniform float randomSeed;
    uniform float maxScreenSpaceKernelRadius;
    uniform float assumedThickness;

    float inverseWorldSpaceScale;

    float getOcclusion( const in vec3 sampleViewPositionFromCenterViewPosition, const in vec3 centerViewNormal ) {
      float viewDistance = length( sampleViewPositionFromCenterViewPosition );
      float scaledDistance = scale * viewDistance;

      return max(0.0, (dot(centerViewNormal, sampleViewPositionFromCenterViewPosition) - minResolution) / scaledDistance - bias) / (1.0 + pow2( scaledDistance * inverseWorldSpaceScale ) );
    }

    // moving costly divides into consts
    const float ANGLE_STEP = PI2 * float( NUM_RINGS ) / float( NUM_SAMPLES );
    const float INV_NUM_SAMPLES = 1.0 / float( NUM_SAMPLES );

    float getAmbientOcclusion( const in vec3 centerViewPosition ) {
      // precompute some variables require in getOcclusion.
      vec3 centerViewNormal = GET_VIEW_NORMAL(centerViewPosition, centerUv);

      // jsfiddle that shows sample pattern: https://jsfiddle.net/a16ff1p7/
      float angle = rand( centerUv + randomSeed ) * PI2;
      INIT_Y_RELATIVE_RADIUS_FROM_WORLD_SPACE(radius, worldSpaceScale, centerViewPosition.z, cameraProjectionMatrix);
      // The maxScreenSpaceKernelRadius parameter is just to keep performance in check.
      radius = min(radius, maxScreenSpaceKernelRadius);
      radius *= INV_NUM_SAMPLES;
      float radiusStep = radius;

      // Adding a random term to radius makes the rendering converge to a much nicer result after TAA without banding
      // artifacts caused by using constant radiuses.
      #if NUM_SAMPLES <= 8
      //    radius += radiusStep * ( rand( centerUv - 0.5 + randomSeed ) - 0.5 );
      // Optimized formula for randomizing the radius step - this has a risk of introducing slight artifacts,
      // but in practice seems to be performing well enough:
          radius += radiusStep * ( fract( angle * 317.0 ) - 0.5 );
      #endif

      // Radius must be at least so high that the same pixel can't be sampled twice from the depth buffer.
      radius = max( radius, invSize.y * 1.5 );

      float occlusionSum = 0.0;
      float weightSum = 0.0;

      float aspect = invSize.x / invSize.y;
      for( int i = 0; i < NUM_SAMPLES; i ++ ) {
        vec2 sampleUv = centerUv + vec2( cos( angle ) * aspect, sin( angle ) ) * radius;
        radius += radiusStep;
        angle += ANGLE_STEP;

        #if DEPTH_MIPS > 1
        // TODO: This code path doesn't work on WebGL 2.0, because it requires constant indices for sampler arrays.
        // We need to unroll the loop or store the textures in an actual mip chain to support WebGL 2.0 here.
        float sampleDepth = GET_DEPTH( (i * DEPTH_MIPS) / NUM_SAMPLES, sampleUv );
        #else
        float sampleDepth = GET_DEPTH( 0, sampleUv );
        #endif

        if (IS_DEPTH_OUT_OF_BOUNDS(sampleDepth)) {
          continue;
        }
        vec3 sampleViewPosition = getViewPosition(sampleUv, getViewZ(sampleDepth));

        vec3 sampleViewPositionFromCenterViewPosition = sampleViewPosition - centerViewPosition;
    #if ASSUME_THICK_GEOMETRY
      #if SIMPLIFIED_SAMPLING_REGION
        // Rectangular prism shaped sampling region.
        float maxZDifferenceInsideSamplingRegion = worldSpaceScale * 1.5;
      #else
        // Spherical sampling region.
        vec3 sampleViewPositionFromCenterViewPositionSquared = sampleViewPositionFromCenterViewPosition * sampleViewPositionFromCenterViewPosition;
        float maxZDifferenceInsideSamplingRegion = sqrt(worldSpaceScale * worldSpaceScale * (1.5 * 1.5) - sampleViewPositionFromCenterViewPositionSquared.x - sampleViewPositionFromCenterViewPositionSquared.y);
      #endif // SIMPLIFIED_SAMPLING_REGION
        float clampedZ = min(sampleViewPositionFromCenterViewPosition.z, maxZDifferenceInsideSamplingRegion);
        if ( sampleViewPositionFromCenterViewPosition.z - clampedZ > assumedThickness ) {
          continue;
        }
        sampleViewPositionFromCenterViewPosition.z = clampedZ;
    #else
      #if SIMPLIFIED_SAMPLING_REGION
        // Rectangular prism shaped sampling region.
        if ( sampleViewPositionFromCenterViewPosition.z > worldSpaceScale * 1.5 )
        {
          continue;
        }
      #else
        // Spherical sampling region.
        float viewDistance = length( sampleViewPositionFromCenterViewPosition );
        // TODO: Try scaling the contribution of the samples as an alternative instead of just cutting them off.
        if ( viewDistance > worldSpaceScale * 1.5 ) {
          continue;
        }
      #endif // SIMPLIFIED_SAMPLING_REGION
    #endif // ASSUME_THICK_GEOMETRY
        float occlusionFromSample = getOcclusion( sampleViewPositionFromCenterViewPosition, centerViewNormal );
        occlusionSum += occlusionFromSample;
        weightSum += 1.0;
      }

      if ( weightSum == 0.0 ) return 0.0;

      return occlusionSum * ( intensity / weightSum );
    }

    void main() {
      inverseWorldSpaceScale = 1.0 / worldSpaceScale;

      // Jitter center UV so that samples don't systematically fall on one side of an edge close to the pixel center.
      // This should convert some systematic artifacts to random noise.
      // This is needed especially when SAO is rendered at reduced resolution.
      centerUv = vUv + (vec2(rand(vUv - 0.7 + randomSeed), rand(vUv - 0.3 + randomSeed)) - 0.5) * invSize;
      

      float centerDepth = GET_DEPTH(0, centerUv);
      if(IS_DEPTH_OUT_OF_BOUNDS(centerDepth)) {
        discard;
      }
      vec3 viewPosition = getViewPosition(centerUv, getViewZ(centerDepth));

    #if OUTPUT_NORMALS == 1
      gl_FragColor = vec4(1.0, packNormalToRGB(GET_VIEW_NORMAL(viewPosition, vUv)));
    #else
      float ambientOcclusion = getAmbientOcclusion( viewPosition );
      gl_FragColor = vec4(1.0 - ambientOcclusion, 0.0, 0.0, 1.0);
    #endif  // else branch of OUTPUT_NORMALS == 1
    }`,
};
